import { apiService } from "@/services/api";
import db from "@/services/offlineDb";
import { enqueueOp } from "@/services/offlineQueue";

const state = {
  tareas: [],
  tareaSeleccionada: null,
  historial: [],

  // Estadísticas
  totalTareas: 0,
  pendientes: 0,
  vencidas: 0,
  rolUsuario: null,

  // Historial meta
  historialMeta: {
    weekStart: null,
    weekEnd: null,
    total: 0,
  },

  loading: false,
  error: null,
};

const mutations = {
  setTareas(state, tareas) {
    state.tareas = tareas;
  },
  setTareaSeleccionada(state, tarea) {
    state.tareaSeleccionada = tarea;
  },
  setHistorial(state, historial) {
    state.historial = historial;
  },
  setEstadisticas(state, { total, pendientes, vencidas, rolUsuario }) {
    state.totalTareas = total;
    state.pendientes = pendientes;
    state.vencidas = vencidas;
    state.rolUsuario = rolUsuario;
  },
  setHistorialMeta(state, { weekStart, weekEnd, total }) {
    state.historialMeta = { weekStart, weekEnd, total };
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },

  // Actualizar tarea en la lista
  updateTareaEnLista(state, tareaActualizada) {
    const index = state.tareas.findIndex(
      (t) => t.id === tareaActualizada.id && t.tipo === tareaActualizada.tipo
    );
    if (index !== -1) {
      state.tareas.splice(index, 1, tareaActualizada);
    }
  },

  // Actualizar item en la tarea seleccionada
  updateItemEnTarea(state, { tareaId, tipo, itemActualizado }) {
    // Actualizar en tareaSeleccionada
    if (
      state.tareaSeleccionada &&
      state.tareaSeleccionada.id === tareaId &&
      state.tareaSeleccionada.tipo === tipo
    ) {
      const itemIndex = state.tareaSeleccionada.items.findIndex(
        (i) => i.id === itemActualizado.id
      );
      if (itemIndex !== -1) {
        state.tareaSeleccionada.items.splice(itemIndex, 1, itemActualizado);
      }
    }

    // Actualizar en lista de tareas
    const tareaIndex = state.tareas.findIndex(
      (t) => t.id === tareaId && t.tipo === tipo
    );
    if (tareaIndex !== -1) {
      const itemIndex = state.tareas[tareaIndex].items.findIndex(
        (i) => i.id === itemActualizado.id
      );
      if (itemIndex !== -1) {
        state.tareas[tareaIndex].items.splice(itemIndex, 1, itemActualizado);
      }
    }
  },

  // Eliminar tarea de la lista
  removeTarea(state, { id, tipo }) {
    state.tareas = state.tareas.filter(
      (t) => !(t.id === id && t.tipo === tipo)
    );
  },

  // Marcar movimiento como revertido
  marcarMovimientoRevertido(state, movimientoId) {
    const index = state.historial.findIndex((m) => m.id === movimientoId);
    if (index !== -1) {
      state.historial[index].puede_revertir = false;
      state.historial[index].status = "reverted";
    }
  },

  clearState(state) {
    state.tareas = [];
    state.tareaSeleccionada = null;
    state.historial = [];
    state.totalTareas = 0;
    state.pendientes = 0;
    state.vencidas = 0;
    state.rolUsuario = null;
    state.historialMeta = { weekStart: null, weekEnd: null, total: 0 };
    state.loading = false;
    state.error = null;
  },
};

const actions = {
  // =============================================
  // TAREAS
  // =============================================

  /**
   * Obtener lista de tareas
   */
  async getTareas(
    { commit },
    { soloPendientes = false, soloVencidas = false, soloHoy = false } = {}
  ) {
    commit("setLoading", true);
    commit("setError", null);

    try {
      const params = new URLSearchParams();
      if (soloPendientes) params.append("solo_pendientes", "true");
      if (soloVencidas) params.append("solo_vencidas", "true");
      if (soloHoy) params.append("solo_hoy", "true");

      const queryString = params.toString();
      const url = queryString
        ? `pendientes/tareas?${queryString}`
        : "pendientes/tareas";

      const response = await apiService.getCustom(url);

      if (response && response.data) {
        commit("setTareas", response.data.tareas || []);
        commit("setEstadisticas", {
          total: response.data.total || 0,
          pendientes: response.data.pendientes || 0,
          vencidas: response.data.vencidas || 0,
          rolUsuario: response.data.rol_usuario || null,
        });
        // Guardar en Dexie para offline
        const tareas = response.data.tareas || [];
        if (tareas.length > 0) {
          db.tareas.bulkPut(tareas).catch(() => {});
        }
      }

      return response;
    } catch (error) {
      // Offline fallback
      if (!navigator.onLine) {
        console.log("📴 Offline fallback: leyendo tareas de Dexie");
        const local = await db.tareas.toArray();
        commit("setTareas", local);
        commit("setEstadisticas", {
          total: local.length,
          pendientes: local.filter((t) => !t.completada).length,
          vencidas: 0,
          rolUsuario: null,
        });
        return { data: { tareas: local } };
      }
      console.error("Error obteniendo tareas:", error);
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  /**
   * Crear nueva tarea
   */
  async postTarea({ commit, dispatch }, tareaData) {
    commit("setError", null);

    // Offline: encolar operación
    if (!navigator.onLine) {
      await enqueueOp(
        "tareas",
        "create",
        tareaData,
        "pendientes/tareas",
        "POST"
      );
      const offlineTarea = {
        _offline: true,
        ...tareaData,
        id: `offline_${Date.now()}`,
      };
      await db.tareas.add(offlineTarea).catch(() => {});
      await dispatch("getTareas");
      return { data: offlineTarea, status: 200 };
    }

    try {
      const response = await apiService.postCustom(
        "pendientes/tareas",
        tareaData
      );

      if (response && response.status >= 200 && response.status < 300) {
        console.log("✅ Tarea creada, recargando lista...");
        // Recargar lista de tareas (getTareas maneja su propio loading)
        await dispatch("getTareas");
      }

      return response;
    } catch (error) {
      console.error("Error creando tarea:", error);
      commit("setError", error.message);
      throw error;
    }
  },

  /**
   * Marcar tarea como completada/no completada
   */
  async toggleTareaCompletada({ commit }, { tareaId, tipo, completada }) {
    try {
      const response = await apiService.patchCustom(
        `pendientes/tareas/${tareaId}?tipo=${tipo}`,
        { completada }
      );

      if (response && response.data) {
        commit("updateTareaEnLista", response.data);

        // Si es la tarea seleccionada, actualizarla también
        commit("setTareaSeleccionada", response.data);
      }

      return response;
    } catch (error) {
      console.error("Error actualizando tarea:", error);
      throw error;
    }
  },

  /**
   * Marcar item del checklist como completado/no completado
   */
  async toggleItemCompletado(
    { commit },
    { tareaId, itemId, tipo, completado }
  ) {
    try {
      const response = await apiService.patchCustom(
        `pendientes/tareas/${tareaId}/items/${itemId}?tipo=${tipo}`,
        { completado }
      );

      if (response && response.data) {
        commit("updateItemEnTarea", {
          tareaId,
          tipo,
          itemActualizado: response.data,
        });
      }

      return response;
    } catch (error) {
      console.error("Error actualizando item:", error);
      throw error;
    }
  },

  /**
   * Eliminar tarea
   */
  async deleteTarea({ commit }, { tareaId, tipo }) {
    try {
      const response = await apiService.deleteCustom(
        `pendientes/tareas/${tareaId}?tipo=${tipo}`
      );

      if (response && (response.status === 200 || response.status === 204)) {
        commit("removeTarea", { id: tareaId, tipo });
      }

      return response;
    } catch (error) {
      console.error("Error eliminando tarea:", error);
      throw error;
    }
  },

  /**
   * Seleccionar tarea para ver checklist
   */
  seleccionarTarea({ commit }, tarea) {
    commit("setTareaSeleccionada", tarea);
  },

  /**
   * Limpiar tarea seleccionada
   */
  clearTareaSeleccionada({ commit }) {
    commit("setTareaSeleccionada", null);
  },

  // =============================================
  // HISTORIAL DE MOVIMIENTOS
  // =============================================

  /**
   * Obtener historial de movimientos
   */
  async getHistorial(
    { commit },
    { dias = 7, entidad = null, empleadoId = null } = {}
  ) {
    commit("setLoading", true);
    commit("setError", null);

    try {
      const params = new URLSearchParams();
      params.append("dias", dias.toString());
      if (entidad) params.append("entidad", entidad);
      if (empleadoId) params.append("empleado_id", empleadoId.toString());

      const response = await apiService.getCustom(
        `pendientes/historial?${params.toString()}`
      );

      if (response && response.data) {
        commit("setHistorial", response.data.movimientos || []);
        commit("setHistorialMeta", {
          weekStart: response.data.week_start,
          weekEnd: response.data.week_end,
          total: response.data.total || 0,
        });
      }

      return response;
    } catch (error) {
      console.error("Error obteniendo historial:", error);
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  /**
   * Deshacer un movimiento
   */
  async deshacerMovimiento({ commit }, movimientoId) {
    try {
      const response = await apiService.postCustom(
        `pendientes/historial/${movimientoId}/deshacer`,
        {}
      );

      if (response && response.status >= 200 && response.status < 300) {
        commit("marcarMovimientoRevertido", movimientoId);
      }

      return response;
    } catch (error) {
      console.error("Error deshaciendo movimiento:", error);
      throw error;
    }
  },
};

const getters = {
  tareas: (state) => state.tareas,
  tareaSeleccionada: (state) => state.tareaSeleccionada,
  historial: (state) => state.historial,

  totalTareas: (state) => state.totalTareas,
  pendientes: (state) => state.pendientes,
  vencidas: (state) => state.vencidas,
  rolUsuario: (state) => state.rolUsuario,

  historialMeta: (state) => state.historialMeta,

  loading: (state) => state.loading,
  error: (state) => state.error,

  // Es gestor según el rol devuelto por el backend
  esGestor: (state, getters, rootState, rootGetters) => {
    // Primero intentar con el rol del backend de tareas
    const rolesGestor = [
      "gestor",
      "admin",
      "gerente",
      "owner",
      "DUENIO",
      "VETERINARIO",
      "TAMBERO",
      "INGENIERO",
    ];
    if (rolesGestor.includes(state.rolUsuario)) {
      return true;
    }
    // Fallback: usar el getter del userTamboStore
    try {
      return rootGetters["userTamboStore/isGestor"] || false;
    } catch {
      return false;
    }
  },

  // Tareas pendientes para hoy (para notificación)
  tareasPendientesHoy: (state) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    return state.tareas.filter((tarea) => {
      if (tarea.completada) return false;
      if (!tarea.fecha) return false;

      const fechaTarea = new Date(tarea.fecha);
      fechaTarea.setHours(0, 0, 0, 0);

      return fechaTarea.getTime() === hoy.getTime();
    });
  },

  // Cantidad de tareas pendientes hoy
  cantidadPendientesHoy: (state, getters) => {
    return getters.tareasPendientesHoy.length;
  },

  // Progreso de la tarea seleccionada
  progresoTareaSeleccionada: (state) => {
    if (!state.tareaSeleccionada || !state.tareaSeleccionada.items) return 0;
    const items = state.tareaSeleccionada.items;
    if (items.length === 0) return state.tareaSeleccionada.completada ? 100 : 0;
    const completados = items.filter((i) => i.completado).length;
    return Math.round((completados / items.length) * 100);
  },

  // Items completados de la tarea seleccionada
  itemsCompletados: (state) => {
    if (!state.tareaSeleccionada || !state.tareaSeleccionada.items) return 0;
    return state.tareaSeleccionada.items.filter((i) => i.completado).length;
  },

  // Total items de la tarea seleccionada
  totalItems: (state) => {
    if (!state.tareaSeleccionada || !state.tareaSeleccionada.items) return 0;
    return state.tareaSeleccionada.items.length;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
